import * as React from 'react';

import { cn } from '../../lib/utils.js';

/**
 * The one look every popup shares, taken from the sign-in modal (AuthGuard in App.tsx): the page behind it stays
 * visible but blurred, washed out and pushed back slightly, under a light tint.
 *
 * Two ways to get it:
 *  - BLURRED_PAGE + MODAL_LAYER when the page behind is rendered by us (AuthGuard, BlurredBackdrop): the page
 *    element itself is blurred, which is what the sign-in modal does.
 *  - MODAL_BACKDROP for overlays over whatever is on screen (Dialog, feedback popup): a backdrop-filter layer
 *    that approximates the same blur and wash-out.
 *
 * Not for overlays over the student exam's drawing canvas: a viewport-wide backdrop-filter over that SVG is a
 * measured paint/composite regression (see drawing-canvas.tsx), so it keeps a plain scrim.
 */
export const BLURRED_PAGE = 'blur-md opacity-60 scale-[0.98] pointer-events-none select-none';
export const MODAL_LAYER = 'bg-slate-900/10 backdrop-blur-[2px]';
// The tint is the sign-in modal's two layers folded into one: the page at 60% over slate-50, under slate-900 at 10%,
// equals 46% of rgb(197 201 206) over the unfaded page.
export const MODAL_BACKDROP = 'bg-[rgb(197_201_206/0.46)] dark:bg-slate-950/50 backdrop-blur-md';

/** A popup card over a blurred, inert copy of `background`. */
export function BlurredBackdrop({ background, children }: { background: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="relative w-full h-screen overflow-hidden bg-slate-50 dark:bg-background">
      <div className={cn('h-full w-full', BLURRED_PAGE)} aria-hidden inert>
        {background}
      </div>
      <div className={cn('absolute inset-0 z-[100] flex items-center justify-center p-4', MODAL_LAYER)}>{children}</div>
    </div>
  );
}
